import React, {useCallback, useMemo, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';

import {useNewsletterDictionary} from 'src/hooks/dictionaries/useNewsletterDictionary';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {
    applyUpdatedData,
    setNewsletters,
} from 'src/modules/create-user/user-bulk-import/services/user-bulk-import.slice';
import {sortNewsletterItems} from 'src/utils/sortNewsletterItems';

import Button, {FlatButton} from '@pb/kit-component-button';
import {CheckBox} from '@pb/kit-component-checkbox';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {Dialog, DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';

import type {CheckBoxChangeData} from 'src/typedef';

type Props = {
    selectedRecordIds: number[],
};

const MODAL_WIDTH = 350;

export const SetNewsletters = ({selectedRecordIds}: Props) => {
    const dispatch = useDispatch();
    const [selectedNewsletters, setSelectedNewsletters] = useState<string[]>([]);
    const [shownModal, setShownModal] = useState(false);
    const {userFieldChange} = useEventTracking();

    const {newsletterDictionary} = useNewsletterDictionary();

    const selectNewsletters = useCallback(() => {
        setShownModal(false);
        dispatch(applyUpdatedData());
        dispatch(setNewsletters({
            ids: selectedRecordIds,
            newsletters: selectedNewsletters,
        }));

        userFieldChange({
            section: 'Validate File',
            parameterName: 'Newsletters',
            viewType: 'Bulk Import Users',
        });
    }, [selectedRecordIds, selectedNewsletters, userFieldChange, dispatch]);

    const handleGroupToggle = useCallback((childIds: string[], defaultId: string, checked: boolean) => {
        setSelectedNewsletters(prev => checked
            ? [...new Set([...prev, defaultId])].sort()
            : prev.filter(c => !childIds.includes(c)),
        );
    }, []);

    const handleGroupChildSelect = useCallback((childIds: string[], code: string) => {
        setSelectedNewsletters(prev => [
            ...prev.filter(c => !childIds.includes(c)),
            code,
        ].sort());
    }, []);

    const handleLeafToggle = useCallback((code: string, checked: boolean) => {
        setSelectedNewsletters(prev => (checked
            ? [...prev, code]
            : prev.filter(c => c !== code)
        ).sort());
    }, []);

    const closeModal = useCallback(() => {
        setShownModal(false);
        setSelectedNewsletters([]);
    }, []);

    const comboboxGroupIndex = useMemo(() =>
        newsletterDictionary.findIndex(item => (item.children?.length ?? 0) > 1),
    [newsletterDictionary]);

    const lastComboValueRef = useRef<string>('');

    const newsletterOptions = useMemo(() => newsletterDictionary.flatMap((item, index) => {
        const childIds = item.children?.map(c => c.id) ?? [];
        if (childIds.length > 1 && index === comboboxGroupIndex) {
            const selectedChild = selectedNewsletters.find(c => childIds.includes(c));
            const defaultChildId = item.children![2].id;
            const comboValue = selectedChild ?? (lastComboValueRef.current || defaultChildId);
            if (selectedChild) {
                lastComboValueRef.current = selectedChild;
            }
            const sortedChildren = sortNewsletterItems(item.children!);
            return [(
                <Group direction="v" gap="xs" key={item.id}>
                    <CheckBox
                        label={item.description}
                        checked={!!selectedChild}
                        onCheckBoxChange={({event}: CheckBoxChangeData) =>
                            handleGroupToggle(childIds, comboValue, event.currentTarget.checked)}
                    />
                    <ComboBox
                        width={300}
                        disabled={!selectedChild}
                        value={comboValue}
                        onChange={(code: string) => handleGroupChildSelect(childIds, code)}>
                        {sortedChildren.map(child => (
                            <ComboboxItem key={child.id} caption={child.description} value={child.id}/>
                        ))}
                    </ComboBox>
                </Group>
            )];
        }
        if (childIds.length) {
            const sortedChildren = sortNewsletterItems(item.children!);
            return (
                <Group key={item.id} style={{paddingTop: 16}} direction="v" gap="m"> {sortedChildren.map(child => (
                    <CheckBox
                        key={child.id}
                        name={child.id}
                        label={child.description}
                        checked={selectedNewsletters.includes(child.id)}
                        onCheckBoxChange={({event}: CheckBoxChangeData) =>
                            handleLeafToggle(child.id, event.currentTarget.checked)}
                    />
                ))}
                </Group>
            );
        }
        return [(
            <CheckBox
                key={item.id}
                name={item.id}
                label={item.description}
                checked={selectedNewsletters.includes(item.id)}
                onCheckBoxChange={({event}: CheckBoxChangeData) =>
                    handleLeafToggle(item.id, event.currentTarget.checked)}
            />
        )];
    }), [newsletterDictionary, comboboxGroupIndex, selectedNewsletters,
        handleGroupToggle, handleGroupChildSelect, handleLeafToggle]);

    return (
        <>
            <FlatButton disabled={false} onClick={() => setShownModal(true)}>
                <strong>
                    Set Newsletters
                </strong>
            </FlatButton>
            <Dialog
                shown={shownModal}
                setShown={closeModal}
                wrapperProps={{style: {width: MODAL_WIDTH}}}>
                <DialogHeader onClose={closeModal}>
                    Set Newsletters
                </DialogHeader>
                <DialogMain style={{padding: 24}}>
                    <Group direction="v">
                        <div style={{width: 300}}>
                            <Label>PitchBook newsletter</Label>
                            {newsletterOptions}
                        </div>
                    </Group>
                </DialogMain>
                <DialogFooter>
                    <Group>
                        <Button variant="Type.PRIMARY" onClick={selectNewsletters}>Apply</Button>
                        <Button variant="Type.SECONDARY" onClick={closeModal}>Cancel</Button>
                    </Group>
                </DialogFooter>
            </Dialog>
        </>
    );
};
